/* Staff PIN dialog — extracted from /app/frontend/src/pages/Staff.js (iter83).
 * Renders the 4-digit (staff) or 6-digit (teacher) PIN setter with QR code
 * and copy-to-clipboard action. Preserves all data-testid attributes so
 * existing e2e tests keep working. */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Key, Copy, Check } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';

export const StaffPinDialog = ({
  open, onOpenChange, pinKind, pinTarget, pinValue, setPinValue,
  copied, copyCredentials, savePin, pinSaving, language, t,
}) => {
  const digits = pinKind === 'teacher' ? 6 : 4;
  const qrValue = pinTarget && new RegExp(`^\\d{${digits}}$`).test(pinValue)
    ? (pinKind === 'teacher'
        ? `teacherpin:${pinTarget.id}:${pinValue}`
        : `pinlogin:${pinTarget.id}:${pinValue}`)
    : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Key size={20} className="text-[#0052FF]" />
            {language === 'en' ? 'Set mobile PIN' : 'Définir le PIN mobile'}
          </DialogTitle>
          <DialogDescription>
            {pinKind === 'teacher'
              ? (language === 'en'
                  ? 'This 6-digit PIN lets the teacher sign in on the mobile app at /m/login (Enseignant tab). They will also need the Teacher ID shown below.'
                  : 'Ce PIN à 6 chiffres permet à l\'enseignant de se connecter à l\'app mobile /m/login (onglet Enseignant). Il aura aussi besoin de l\'Identifiant ci-dessous.')
              : (language === 'en'
                  ? 'This 4-digit PIN lets the staff member sign in on the mobile app at /m/login. They will also need the Agent ID shown below.'
                  : 'Ce PIN à 4 chiffres permet à l\'agent de se connecter à l\'app mobile /m/login. Il aura aussi besoin de l\'Identifiant agent ci-dessous.')}
          </DialogDescription>
        </DialogHeader>
        {pinTarget && (
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] p-3 rounded text-sm">
              <p className="font-semibold">{pinTarget.first_name} {pinTarget.last_name}</p>
              <p className="text-xs text-[#64748B]">{pinKind === 'teacher' ? (pinTarget.subject || 'Enseignant') : pinTarget.staff_type}</p>
            </div>
            <div>
              <Label className="text-xs uppercase">
                {pinKind === 'teacher'
                  ? (language === 'en' ? 'Teacher ID' : 'Identifiant enseignant')
                  : (language === 'en' ? 'Agent ID' : 'Identifiant agent')}
              </Label>
              <Input value={pinTarget.id} readOnly className="font-mono text-xs" data-testid="pin-dialog-staff-id" />
            </div>
            <div>
              <Label className="text-xs uppercase">
                PIN ({digits} {language === 'en' ? 'digits' : 'chiffres'})
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern={`\\d{${digits}}`}
                maxLength={digits}
                value={pinValue}
                onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, digits))}
                className="text-center tracking-[0.5em] text-2xl font-mono"
                data-testid="pin-dialog-pin"
              />
            </div>

            {qrValue && (
              <div className="bg-white border-2 border-dashed border-[#0052FF] rounded-lg p-4 flex flex-col items-center gap-2" data-testid="pin-dialog-qr">
                <p className="text-xs text-[#64748B] text-center">
                  {language === 'en'
                    ? 'Scan from /m/login → instant connect'
                    : 'Scannez ce QR depuis /m/login → connexion instantanée'}
                </p>
                <QRCodeSVG value={qrValue} size={160} level="M" />
              </div>
            )}

            <Button onClick={copyCredentials} variant="outline" className="w-full" data-testid="pin-dialog-copy">
              {copied ? <Check size={14} className="mr-2 text-green-600" /> : <Copy size={14} className="mr-2" />}
              {copied
                ? (language === 'en' ? 'Copied!' : 'Copié !')
                : (language === 'en' ? 'Copy ID + PIN' : 'Copier identifiant + PIN')}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button onClick={savePin} disabled={pinSaving} data-testid="pin-dialog-save">
            {pinSaving ? '...' : (language === 'en' ? 'Save PIN' : 'Enregistrer le PIN')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
